<div class="relative w-auto">
  <!-- Trigger -->
  <button
    type="button"
    (click)="toggle()"
    class="flex items-center w-full justify-between gap-4 h-11 pl-3 pr-2.5 text-sm border rounded-lg bg-white transition-colors cursor-pointer min-w-36 disabled:opacity-60 disabled:cursor-default disabled:hover:bg-white"
    [ngClass]="
      open()
        ? 'border-accent-500 ring-2 ring-accent-200'
        : error() && !value()
          ? 'border-red-400 ring-2 ring-red-100'
          : 'border-surface-300 hover:bg-surface-50'
    "
    [disabled]="disabled()"
  >
    <span
      [class]="label() ? 'text-navy-900 whitespace-nowrap' : 'text-slate-400 whitespace-nowrap'"
    >
      {{ label() || placeholder() }}
    </span>
    <img src="/icons/calendar-icon.svg" class="w-4 h-4 shrink-0" alt="" />
  </button>

  <!-- Dropdown -->
  @if (open()) {
    <div class="fixed inset-0 z-40 max-w-20" (click)="tryClose()"></div>
    <div
      class="fixed z-50 bg-white rounded-xl border border-surface-300 shadow-lg p-4 min-w-68"
      [style.top.px]="dropdownTop()"
      [style.left.px]="dropdownLeft()"
      (pointerdown)="onPanelPointerDown($event)"
    >
      <!-- Month navigation -->
      <div class="flex items-center justify-between mb-2">
        <button
          type="button"
          (click)="prev()"
          class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-200 transition-colors cursor-pointer"
        >
          <img src="/icons/chevron-left-icon.svg" class="w-4 h-4" alt="" />
        </button>
        <span class="text-sm font-semibold text-navy-900 px-2">{{ monthLabel() }}</span>
        <button
          type="button"
          (click)="next()"
          class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-200 transition-colors cursor-pointer"
        >
          <img src="/icons/chevron-right-icon.svg" class="w-4 h-4" alt="" />
        </button>
      </div>

      <!-- Day headers -->
      <div class="grid grid-cols-[repeat(7,2rem)] mb-1">
        @for (h of DAY_HEADERS; track $index) {
          <div
            class="h-7 flex items-center justify-center text-2xs font-semibold text-slate-400 uppercase"
          >
            {{ h }}
          </div>
        }
      </div>

      <!-- Calendar grid -->
      @for (week of weeks(); track $index) {
        <div class="grid grid-cols-[repeat(7,2rem)] mb-1">
          @for (day of week; track $index) {
            @if (day) {
              <div class="h-8 flex items-center justify-center">
                <button
                  type="button"
                  (click)="selectDay(day)"
                  class="w-7 h-7 flex items-center justify-center rounded-full text-xs transition-colors cursor-pointer"
                  [ngClass]="{
                    'bg-primary-600 text-white font-medium': isSelected(day),
                    'text-navy-900 hover:bg-surface-200': !isSelected(day),
                    'ring-1 ring-inset ring-accent-400': isToday(day) && !isSelected(day),
                  }"
                >
                  {{ day.getDate() }}
                </button>
              </div>
            } @else {
              <div class="h-8"></div>
            }
          }
        </div>
      }

      <!-- Time selector -->
      @if (!hideTime()) {
        <!-- Time: hour + minute lists and AM/PM (JCCA-115 / 124) -->
        <div class="mt-3 pt-3 border-t border-surface-200">
          <div class="flex items-center justify-between mb-2">
            <p
              class="text-2xs font-semibold uppercase tracking-wider"
              [ngClass]="showTimeError() ? 'text-red-500' : 'text-slate-400'"
            >
              Time{{ showTimeError() ? ' — required' : '' }}
            </p>
            <button
              type="button"
              (click)="setNow()"
              class="text-2xs font-semibold text-accent-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              Now
            </button>
          </div>
          <div class="flex items-center justify-center gap-2">
            <div class="relative" data-time-list>
              <button
                type="button"
                aria-label="Hour"
                aria-haspopup="listbox"
                [attr.aria-expanded]="openTimeList() === 'hour'"
                (click)="toggleTimeList('hour')"
                class="w-16 h-10 flex items-center justify-center rounded-lg border bg-white text-sm font-semibold transition-colors cursor-pointer"
                [ngClass]="
                  openTimeList() === 'hour'
                    ? 'border-accent-500 ring-2 ring-accent-200'
                    : showTimeError() && !hour()
                      ? 'border-red-400 ring-1 ring-red-200'
                      : 'border-surface-300 hover:bg-surface-50'
                "
              >
                <span [ngClass]="hour() ? 'text-navy-900' : 'text-slate-400'">{{
                  hour() || 'hh'
                }}</span>
              </button>
              @if (openTimeList() === 'hour') {
                <!-- Opens upward over the calendar, so it always stays inside the panel -->
                <div
                  role="listbox"
                  [attr.aria-label]="'Hour'"
                  class="absolute bottom-full left-0 mb-1 w-16 max-h-48 overflow-y-auto bg-white border border-surface-300 rounded-lg shadow-lg py-1 z-10"
                >
                  @for (v of HOURS; track v) {
                    <button
                      type="button"
                      role="option"
                      [attr.aria-selected]="v === hour()"
                      [attr.data-time-option-selected]="v === hour() ? '' : null"
                      (click)="onHourChange(v)"
                      class="w-full py-1.5 text-sm text-center transition-colors cursor-pointer"
                      [ngClass]="
                        v === hour()
                          ? 'bg-accent-500 text-white font-semibold'
                          : 'text-navy-900 hover:bg-surface-100'
                      "
                    >
                      {{ v }}
                    </button>
                  }
                </div>
              }
            </div>
            <span class="text-lg font-bold text-navy-600 select-none leading-none">:</span>
            <div class="relative" data-time-list>
              <button
                type="button"
                aria-label="Minute"
                aria-haspopup="listbox"
                [attr.aria-expanded]="openTimeList() === 'minute'"
                (click)="toggleTimeList('minute')"
                class="w-16 h-10 flex items-center justify-center rounded-lg border bg-white text-sm font-semibold transition-colors cursor-pointer"
                [ngClass]="
                  openTimeList() === 'minute'
                    ? 'border-accent-500 ring-2 ring-accent-200'
                    : showTimeError() && !minute()
                      ? 'border-red-400 ring-1 ring-red-200'
                      : 'border-surface-300 hover:bg-surface-50'
                "
              >
                <span [ngClass]="minute() ? 'text-navy-900' : 'text-slate-400'">{{
                  minute() || 'mm'
                }}</span>
              </button>
              @if (openTimeList() === 'minute') {
                <!-- Opens upward over the calendar, so it always stays inside the panel -->
                <div
                  role="listbox"
                  [attr.aria-label]="'Minute'"
                  class="absolute bottom-full left-0 mb-1 w-16 max-h-48 overflow-y-auto bg-white border border-surface-300 rounded-lg shadow-lg py-1 z-10"
                >
                  @for (v of MINUTES; track v) {
                    <button
                      type="button"
                      role="option"
                      [attr.aria-selected]="v === minute()"
                      [attr.data-time-option-selected]="v === minute() ? '' : null"
                      (click)="onMinuteChange(v)"
                      class="w-full py-1.5 text-sm text-center transition-colors cursor-pointer"
                      [ngClass]="
                        v === minute()
                          ? 'bg-accent-500 text-white font-semibold'
                          : 'text-navy-900 hover:bg-surface-100'
                      "
                    >
                      {{ v }}
                    </button>
                  }
                </div>
              }
            </div>
            <div class="flex items-center h-10 bg-slate-100 rounded-3xl p-1 gap-1 ml-1">
              @for (p of ['AM', 'PM']; track p) {
                <button
                  type="button"
                  (click)="setPeriod($any(p))"
                  [ngClass]="
                    period() === p
                      ? 'bg-white text-navy-900 shadow-sm'
                      : 'text-slate-500 hover:text-navy-700'
                  "
                  class="h-full px-3 text-xs font-semibold rounded-full transition-all cursor-pointer"
                >
                  {{ p }}
                </button>
              }
            </div>
          </div>
        </div>
      }

      <!-- Footer -->
      <div class="mt-3 pt-3 border-t border-surface-200 flex items-center justify-between">
        <button
          type="button"
          (click)="clear()"
          class="text-sm text-accent-500 hover:text-primary-600 font-semibold transition-colors cursor-pointer"
        >
          Clear
        </button>
        @if (!hideTime()) {
          <button
            type="button"
            (click)="open.set(false)"
            [disabled]="!selectedDate() || !timeSet()"
            class="px-4 py-1.5 text-sm font-semibold text-white rounded-lg transition-opacity"
            [class]="
              !selectedDate() || !timeSet()
                ? 'bg-slate-300 cursor-not-allowed opacity-60'
                : 'bg-accent-500 hover:opacity-90 cursor-pointer'
            "
          >
            Done
          </button>
        }
      </div>
    </div>
  }
</div>
